import { useState, useEffect, useMemo } from 'react';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { Player, Round } from '../types';
import { calculateResults, calculateOverallWinners, PlayerResult } from '../utils/gameUtils';
import { useToast } from '../contexts/ToastContext';

export const useResults = (roomCode: string | null, players: Player[]) => {
  const [allRounds, setAllRounds] = useState<Round[]>([]);
  const [loading, setLoading] = useState(false);
  const { showError } = useToast();

  // Fetch all rounds for summary
  useEffect(() => {
    if (!roomCode) {
      setAllRounds([]);
      return;
    }

    const fetchRounds = async () => {
      setLoading(true);
      try {
        const roundsRef = collection(db, 'rooms', roomCode, 'rounds');
        const roundsQuery = query(roundsRef, orderBy('index'));
        const roundsSnap = await getDocs(roundsQuery);
        
        const rounds = roundsSnap.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as Round[];
        
        setAllRounds(rounds);
      } catch (error: any) {
        console.error('Error fetching rounds:', error);
        showError(error.message || 'Error al obtener los resultados');
      } finally {
        setLoading(false);
      }
    };

    fetchRounds();
  }, [roomCode, showError]);

  // Calculate results for a specific round
  const getRoundResults = (round: Round): PlayerResult[] => {
    return calculateResults(players, round.tally);
  };

  // Calculate overall game results
  const overallResults = useMemo(() => {
    if (players.length === 0 || allRounds.length === 0) {
      return { winners: [], totalVotes: {} };
    }

    return calculateOverallWinners(players, allRounds);
  }, [players, allRounds]);

  // Get summary data for all rounds
  const roundsSummary = useMemo(() => {
    return allRounds.map(round => {
      const results = getRoundResults(round);
      const totalVotes = Object.values(round.tally).reduce((sum, votes) => sum + votes, 0);
      
      return {
        round,
        results,
        totalVotes,
        winners: results.filter(r => r.isWinner)
      };
    });
  }, [allRounds, players]);

  // Calculate player statistics across all rounds
  const playerStats = useMemo(() => {
    const stats: Record<string, {
      player: Player;
      totalVotes: number;
      roundsWon: number;
      averagePercentage: number;
      bestRound: { round: number; percentage: number } | null;
    }> = {};

    players.forEach(player => {
      stats[player.id] = {
        player,
        totalVotes: 0,
        roundsWon: 0,
        averagePercentage: 0,
        bestRound: null
      };
    });

    roundsSummary.forEach((summary, index) => {
      summary.results.forEach(result => {
        const playerStat = stats[result.player.id];
        if (playerStat) {
          playerStat.totalVotes += result.votes;
          
          if (result.isWinner) {
            playerStat.roundsWon++;
          }

          // Track best round
          if (!playerStat.bestRound || result.percentage > playerStat.bestRound.percentage) {
            playerStat.bestRound = {
              round: index + 1,
              percentage: result.percentage
            };
          }
        }
      });
    });

    // Calculate average percentage
    Object.values(stats).forEach(stat => {
      const playerRounds = roundsSummary.filter(summary => 
        summary.results.some(r => r.player.id === stat.player.id)
      );
      
      if (playerRounds.length > 0) {
        const totalPercentage = playerRounds.reduce((sum, summary) => {
          const playerResult = summary.results.find(r => r.player.id === stat.player.id);
          return sum + (playerResult?.percentage || 0);
        }, 0);
        
        stat.averagePercentage = totalPercentage / playerRounds.length;
      }
    });

    return Object.values(stats).sort((a, b) => b.totalVotes - a.totalVotes);
  }, [players, roundsSummary]);

  return {
    allRounds,
    loading,
    getRoundResults,
    overallResults,
    roundsSummary,
    playerStats
  };
};